import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, FileText, Clock, CheckCircle } from 'lucide-react';

export default function SummarySchedule() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadSummary();
  }, [projectId]);

  const loadSummary = async () => {
    try {
      // Fetch events for this project
      const eventsResponse = await api.events.getByProject(projectId);
      const events = eventsResponse.data || [];
      
      // Fetch notices and claims from events
      const notices = [];
      const claims = [];
      
      for (const event of events) {
        if (event.notice_id) {
          try {
            const noticeRes = await api.notices.getById(event.notice_id);
            if (noticeRes.data) {
              notices.push(noticeRes.data);
              // Check if notice has a claim
              if (noticeRes.data.claim_id) {
                try {
                  const claimRes = await api.claims.getById(noticeRes.data.claim_id);
                  if (claimRes.data) {
                    claims.push(claimRes.data);
                  }
                } catch (e) {
                  console.log('Claim not found:', noticeRes.data.claim_id);
                }
              }
            }
          } catch (e) {
            console.log('Notice not found:', event.notice_id);
          }
        }
      }
      
      setSummary({
        events,
        notices,
        claims
      });
    } catch (error) {
      console.error('Failed to load summary:', error);
      toast.error('Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-slate-500 font-mono">Loading summary...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!summary) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-barlow font-bold uppercase mb-2">No Data Available</h3>
            <Button onClick={() => navigate(`/projects/${projectId}`)} className="mt-4">
              Back to Project
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-orange-100 text-orange-700 border-orange-200',
      notice_created: 'bg-blue-100 text-blue-700 border-blue-200',
      shared: 'bg-blue-100 text-blue-700 border-blue-200',
      submitted: 'bg-purple-100 text-purple-700 border-purple-200',
      in_adjudication: 'bg-blue-100 text-blue-700 border-blue-200',
      amendments_required: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return styles[status] || styles.draft;
  };

  const allItems = [
    ...summary.events.map(e => ({ ...e, type: 'event', status: e.status })),
    ...summary.notices.map(n => ({ ...n, type: 'notice', status: n.status })),
    ...summary.claims.map(c => ({ ...c, type: 'claim', status: c.status })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredItems = filter === 'all' ? allItems : allItems.filter(item => item.type === filter);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="summary-schedule">
        <div className="mb-8">
          <Button
            onClick={() => navigate(`/projects/${projectId}`)}
            variant="ghost"
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Project
          </Button>
          <h1 className="text-4xl md:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">Summary Schedule</h1>
          <p className="text-slate-600">Overview of all events, notices, and claims</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Events</p>
            <p className="text-3xl font-barlow font-bold">{summary.events.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notices</p>
            <p className="text-3xl font-barlow font-bold">{summary.notices.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Claims</p>
            <p className="text-3xl font-barlow font-bold">{summary.claims.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Approved</p>
            <p className="text-3xl font-barlow font-bold text-emerald-600">
              {summary.claims.filter(c => c.status === 'approved').length}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[
            { key: 'all', label: 'All Items' },
            { key: 'event', label: 'Events' },
            { key: 'notice', label: 'Notices' },
            { key: 'claim', label: 'Claims' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors ${
                filter === tab.key
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Description</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredItems.map((item) => (
                  <tr key={`${item.type}-${item.id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <Badge
                        className={`
                          ${item.type === 'event' ? 'bg-orange-100 text-orange-700 border-orange-200' : ''}
                          ${item.type === 'notice' ? 'bg-blue-100 text-blue-700 border-blue-200' : ''}
                          ${item.type === 'claim' ? 'bg-purple-100 text-purple-700 border-purple-200' : ''}
                          border
                        `}
                      >
                        {item.type.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-md">
                        {item.type === 'event' && (
                          <p className="font-medium text-slate-900">{item.event_type}</p>
                        )}
                        {item.type === 'notice' && (
                          <p className="font-medium text-slate-900">Notice for Event {item.event_id.substring(0, 8)}</p>
                        )}
                        {item.type === 'claim' && (
                          <p className="font-medium text-slate-900">EOT Claim {item.id.substring(0, 8)}</p>
                        )}
                        <p className="text-sm text-slate-500 line-clamp-1">
                          {item.narrative || item.edited_content?.substring(0, 80) + '...'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={`${getStatusBadge(item.status)} border`}>
                        {item.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono text-slate-600">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        onClick={() => {
                          if (item.type === 'event') navigate(`/events/${item.id}`);
                          if (item.type === 'notice') navigate(`/notices/${item.id}`);
                          if (item.type === 'claim') navigate(`/claims/${item.id}`);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-secondary hover:text-secondary/90"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredItems.length === 0 && (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-600">No items to display</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
